import { useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { Box } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";

/**
 * MiFileInput component allows users to upload files by clicking a button.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} [props.multiple=true] - Allows multiple file selection if true.
 * @param {boolean} [props.isVisible=true] - Controls the visibility of the component.
 * @param {string} [props.title="Upload Files"] - The title displayed on the upload button.
 * @param {Function} [props.onChange=() => null] - Callback function to handle file input change event.
 * @param {Function} [props.disabled=false] - Disables the file input if true.
 * @param {string} [props.customClassName] - Optional class name
 * @param {Object} [props.startIcon] - Optional object that contains the icon config for the upload button
 * @param {string} [props.startIcon.path] - The icon path
 * @param {Number} [props.startIcon.iconSize] - The icons size
 * @param {string} [props.startIcon.iconColor] - The icons color
 * @param {string} [props.testId] - Optional test ID for testing purposes.
 * @param {string} [props.variant=BUTTON_VARIANTS.OUTLINED] - The variant of the upload button.
 * @param {string} [props.acceptFiles] - Comma-separated string that defines the file types the file input should accept.
 * @example
 * <MiFileInput multiple={false} onChange={(e) => console.log(e.target.files)} />
 *
 * @returns {JSX.Element} The rendered MiFileInput component.
 */
export const MiFileInput = ({
  isVisible = true,
  title = "Upload Files",
  multiple = true,
  onChange = () => null,
  disabled = false,
  customClassName,
  startIcon,
  testId,
  variant = BUTTON_VARIANTS.OUTLINED,
  acceptFiles,
}) => {
  const { classes } = useStyles();

  const fileInputRef = useRef(null);

  const handleClick = () => {
    // Reset before opening instead of after change: clearing the input after selection
    // invalidates the selected File on iOS/WebKit and uploads end up with an empty body.
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  return (
    <Box {...(!isVisible && { sx: { display: "none" } })}>
      <MiButton
        title={title}
        variant={variant}
        onClick={handleClick}
        disabled={disabled}
        data-testid={`${testId}-browse-files`}
        className={customClassName}
        startIcon={startIcon}
      />
      <input
        ref={fileInputRef}
        className={classes.fileInput}
        type="file"
        data-testid={`${testId}-input`}
        onChange={onChange}
        multiple={multiple}
        accept={acceptFiles}
      />
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  fileInput: {
    display: "none",
  },
}));
